import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Stack,
  Typography,
  Alert,
  FormGroup,
  FormControlLabel,
  Switch,
  Chip,
  AlertTitle,
} from '@mui/material';
import { Modal } from '../../../dialogs/useModal';
import styled from '@emotion/styled';
import { theme } from '../../../theme';
import { SnapshotStorage, watchPreviewContent } from '@tiptap-pro/extension-snapshot';
import { usePreviewGuideEditor } from '../tiptap/useGuideEditor';
import { GuideEditor } from '../tiptap/GuideEditor';
import { useGuideCmsContext } from '../GuideCmsContext';
import { formatDate, formatDateOnly } from '../../../Formatters';
import { SnapshotCompareStorage } from '@tiptap-pro/extension-snapshot-compare';
import { CREATE_NEW_GUIDE_CONTENT_VERSION } from '../content.graphql';
import { useMutation } from '@apollo/client';
import { useUpdateNumberedNodesOnEditor } from '../tiptap/numbering';
import LoadingSpinner from '../../../LoadingSpinner';
import { Launch, Restore, Visibility } from '@mui/icons-material';
import { useFeatureFlags } from '../../../unleash';
import { useConfig } from '../../../env';
import { GuideCmsInfo } from '../types';
import { TiptapDocumentItemFragment } from '../../../__generated__/graphql';

const VersionItem = styled(Paper)<{ selected?: boolean }>`
  padding: ${theme.spacing(1)};
  cursor: pointer;
  ${({ selected }) => (selected ? `border-left: 4px solid ${theme.palette.action.active};` : '')}
  background-color: ${({ selected }) => (selected ? theme.palette.action.selected : 'transparent')};
  &:hover {
    background-color: ${theme.palette.action.hover};
  }
`;

const StyledLayout = styled.div`
  display: grid;
  height: 100%;
  grid-template-columns: auto 400px;
`;
const StyledSidebarItems = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledMainContainer = styled.div`
  display: grid;
  padding: 16px;
  grid-template-rows: auto 1fr;
  overflow: hidden;
`;

const StyledGuideEditorContainer = styled.div`
  overflow-y: auto;
  max-height: 100%;
  padding-bottom: 32px;
`;

const StyledSidebarContainer = styled.div`
  border-left: 1px solid ${theme.palette.divider};
  padding: 16px;
  display: grid;
  grid-gap: 16px;
  width: 100%;
  max-height: 100%;
  overflow: hidden;
  grid-template-rows: auto 1fr auto;
`;

export interface HistoryDialogResult {
  version: number;
  action: 'navigate' | 'restore';
}

export interface HistoryDialogProps {}

export const HistoryDialog: FC<Modal<HistoryDialogProps, HistoryDialogResult>> = (modal) => {
  const { cmsSnapshotCompare } = useFeatureFlags();
  const config = useConfig();
  const { editor: editorInContext, provider, guideId, guide, tiptapDocument, isArchive, release } = useGuideCmsContext();
  const [createNewContentVersion, { loading: isCreatingNewContentVersion }] = useMutation(CREATE_NEW_GUIDE_CONTENT_VERSION);

  const {
    close,
    data: {},
  } = modal;

  const handleOnClose = () => {
    close();
  };
  const isCurrentTiptapDocument = guide?.tiptapDocument?.documentName === tiptapDocument?.documentName;
  const updateNumberedNodesOnEditor = useUpdateNumberedNodesOnEditor();
  const { editor } = usePreviewGuideEditor();
  const snapshot = editorInContext?.storage.snapshot as SnapshotStorage | undefined;
  const snapshotCompare = editor?.storage.snapshotCompare as SnapshotCompareStorage | undefined;
  const latestVersion = snapshot?.versions[snapshot?.versions.length - 1]?.version;

  let initialSelectedVersion = -1;
  if (tiptapDocument?.documentVersion && tiptapDocument?.documentVersion > 0) {
    initialSelectedVersion = tiptapDocument?.documentVersion;
  } else if (latestVersion) {
    initialSelectedVersion = latestVersion;
  }

  const [selectedVersion, setSelectedVersion] = useState<number>(initialSelectedVersion);
  const [isDiffing, setIsDiffing] = useState<boolean>(false);
  const [diffErrorMessage, setDiffErrorMessage] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const shouldShowDiff = isDiffing && selectedVersion > 0;

  useEffect(() => {
    if (modal.isOpen && provider) {
      const unbindContentWatcher = watchPreviewContent(provider, (content) => {
        if (editor && !shouldShowDiff) {
          editor.commands.setContent(content, { emitUpdate: false });
          updateNumberedNodesOnEditor(editor);
          setIsLoadingContent(false);
        }
      });

      return () => {
        unbindContentWatcher();
      };
    }
  }, [modal.isOpen, provider, editor, shouldShowDiff]);

  // Prevent scrolling when modal is open
  useScrollPrevention(modal);

  useEffect(() => {
    if (!editor) {
      return;
    }
    editor.commands.clearContent();
    setIsLoadingContent(true);
    setDiffErrorMessage('');
    if (snapshotCompare?.isPreviewing) {
      if (!shouldShowDiff) {
        editor.chain().hideDiff().run();
      }
    }
    if (shouldShowDiff) {
      const fromVersion = selectedVersion - 1;
      const toVersion = selectedVersion;
      editor.commands.hideDiff();
      editor.commands.compareVersions({
        fromVersion,
        toVersion,
        onCompare: (context) => {
          if (context.error) {
            setIsLoadingContent(false);
            setDiffErrorMessage(context.error.message);
            console.error(`Failed to diff ${toVersion} with ${fromVersion}`, context.error);
            return;
          }
          if ('editor' in context) {
            context.editor.commands.showDiff(context.tr);
            updateNumberedNodesOnEditor(editor);
            setIsLoadingContent(false);
          }
        },
      });
    } else {
      provider?.sendStateless(
        JSON.stringify({
          action: 'version.preview',
          version: selectedVersion,
        }),
      );
    }
  }, [selectedVersion, isDiffing]);

  if (!provider || !editor || !editorInContext || !tiptapDocument) {
    return null;
  }

  const reversedVersions = useMemo(() => snapshot?.versions?.slice().reverse() ?? [], [snapshot?.versions]);
  const handleRevertToVersion = async (version: number | null) => {
    if (version === null) {
      return;
    }
    const comment = `Gjenopprettet fra versjon ${version}`;
    const success = editorInContext.commands.revertToVersion(version, comment, 'Ulagrede endringer før gjenoppretting');
    if (!success) {
      return;
    }
    setTimeout(async () => {
      // Allow some time before the new version is created in the cloud
      await createNewContentVersion({ variables: { input: { id: guideId, comment } } });
    }, 1000);
  };

  const handleVersionChange = (newVersion: number) => {
    setSelectedVersion(newVersion);
  };

  let previewUrl = `${config?.portalUrl}/dokument/${guide?.docId}?mode=preview&tiptapDocumentVersion=${selectedVersion}`;
  if (isArchive) {
    previewUrl = `${config?.portalUrl}/dokument/${guide?.docId}?mode=preview&version=${release?.publicVersion}&tiptapDocumentVersion=${selectedVersion}`;
  }
  return (
    <Dialog open={true} onClose={handleOnClose} maxWidth="xl" fullWidth={true} scroll="paper">
      <DialogTitle>
        <Stack direction="row" justifyContent={'space-between'}>
          Tidligere versjoner av innholdet
          <Button variant="outlined" color="secondary" href={previewUrl} component="a" target="_blank" startIcon={<Visibility />}>
            Forhåndsvisning
          </Button>
        </Stack>

        {snapshotCompare?.isPreviewing &&
          !isLoadingContent &&
          (diffErrorMessage ? (
            <div style={{ display: 'inline-block' }}>
              <Alert severity="error" sx={{ mt: 1 }}>
                Klarte ikke sammenligne versjon {selectedVersion} og versjon {selectedVersion - 1}. <br />
                {diffErrorMessage}
              </Alert>
            </div>
          ) : (
            <div style={{ display: 'inline-block' }}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Sammenligner versjon {selectedVersion} og versjon {selectedVersion - 1}
              </Alert>
            </div>
          ))}
      </DialogTitle>
      <DialogContent sx={{ height: '90vh', overflow: 'hidden', padding: 0 }} dividers>
        <StyledLayout>
          <StyledMainContainer>
            <StyledGuideEditorContainer>
              {isLoadingContent && <LoadingSpinner />}
              {!isLoadingContent && !isArchive && <PublishInfo guide={guide} tiptapDocument={tiptapDocument} tiptapVersion={selectedVersion} />}
              <GuideEditor guideId={guideId} editor={editor} />
            </StyledGuideEditorContainer>
          </StyledMainContainer>

          <StyledSidebarContainer>
            {cmsSnapshotCompare && (
              <FormGroup sx={{ display: 'inline-block' }}>
                <FormControlLabel
                  control={<Switch checked={isDiffing} onChange={() => setIsDiffing(!isDiffing)} />}
                  label="Sammenlign med forrige versjon"
                />
              </FormGroup>
            )}

            <StyledSidebarItems>
              {reversedVersions.map((version) => (
                <VersionItem
                  elevation={2}
                  key={version.version}
                  onClick={() => handleVersionChange(version.version)}
                  selected={selectedVersion === version.version}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="subtitle2" style={{ display: 'inline-block' }}>
                      <PublishVersionBadge guide={guide} tiptapDocument={tiptapDocument} tiptapVersion={version.version} /> Versjon {version.version}{' '}
                      {version.version === initialSelectedVersion && (
                        <Typography variant="body2" style={{ display: 'inline-block' }}>
                          (gjeldende)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" style={{ display: 'inline-block' }}>
                      {formatDate(new Date(version.date))}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">{version.name}</Typography>
                  <Stack justifyContent="flex-end" direction="row">
                    <TiptapVersionInfo guide={guide} tiptapDocument={tiptapDocument} tiptapVersion={version.version} />
                  </Stack>
                </VersionItem>
              ))}
            </StyledSidebarItems>
            {(isCurrentTiptapDocument || isArchive) && (
              <Stack gap={1}>
                <Button
                  startIcon={<Restore />}
                  disabled={
                    selectedVersion === tiptapDocument?.documentVersion ||
                    (isArchive && selectedVersion === latestVersion) ||
                    isCreatingNewContentVersion
                  }
                  onClick={() => handleRevertToVersion(selectedVersion)}
                  size="medium"
                  variant="contained">
                  Gjenopprett versjon {selectedVersion}
                </Button>
              </Stack>
            )}
          </StyledSidebarContainer>
        </StyledLayout>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleOnClose}>
          Avbryt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PublishVersionBadge: FC<{ guide: GuideCmsInfo; tiptapDocument: TiptapDocumentItemFragment; tiptapVersion: number }> = ({
  guide,
  tiptapDocument,
  tiptapVersion,
}) => {
  const documentName = tiptapDocument?.documentName;
  const release = guide?.releases?.find(
    (r) => r?.guide?.tiptapDocument?.documentName === documentName && r?.guide?.tiptapDocument?.documentVersion === tiptapVersion,
  );
  if (!release) return null;
  return <Chip label={release?.publicVersion} size="small" variant="filled" color="success" />;
};

const TiptapVersionInfo: FC<{ guide: GuideCmsInfo; tiptapDocument: TiptapDocumentItemFragment; tiptapVersion: number }> = ({
  guide,
  tiptapDocument,
  tiptapVersion,
}) => {
  const documentVersion = tiptapDocument.documentVersions?.find((v) => v?.documentVersion === tiptapVersion);
  if (!documentVersion) return null;
  return documentVersion.createdBy?.name;
};

const PublishInfo: FC<{ guide: GuideCmsInfo; tiptapDocument: TiptapDocumentItemFragment; tiptapVersion: number }> = ({
  guide,
  tiptapDocument,
  tiptapVersion,
}) => {
  const config = useConfig();
  const documentName = tiptapDocument?.documentName;
  const release = guide?.releases?.find(
    (r) => r?.guide?.tiptapDocument?.documentName === documentName && r?.guide?.tiptapDocument?.documentVersion === tiptapVersion,
  );
  if (!release) return null;
  return (
    <Alert
      action={
        <Button
          href={`${config?.portalUrl}/dokument/${guide?.docId}?version=${release.publicVersion}`}
          color="inherit"
          endIcon={<Launch />}
          target="_blank">
          Åpne i Byggforsk.no
        </Button>
      }>
      <AlertTitle>Publisert som versjon {release.publicVersion}</AlertTitle>
      Dato: {formatDateOnly(new Date(release.createdAt))}
    </Alert>
  );
};

function useScrollPrevention(modal: Modal<HistoryDialogProps, HistoryDialogResult>) {
  useEffect(() => {
    if (modal.isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [modal.isOpen]);
}
